import { useRef, useState } from "react";
import { MdClose, MdMenu } from "react-icons/md";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { IoIosArrowDown } from "react-icons/io";

type NavigationItem = {
  label: string;
  children?: string[];
};

const NAV_ITEMS: NavigationItem[] = [
  { label: "Home" },
  { label: "Alumni directory" },
  { label: "Thoughtware and insights" },
  { label: "Events" },  
  { label: "Conversations" },
  {
    label: "Careers",
    children: ["Deloitte jobs", "Marketplace jobs"],
  },
  { label: "Helpdesk" },
];

const LandingHeader = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDesktopCareersOpen, setIsDesktopCareersOpen] = useState(false);
  const [isMobileCareersOpen, setIsMobileCareersOpen] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const desktopCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleDesktopCareersEnter = () => {
    if (desktopCloseTimeoutRef.current) {
      clearTimeout(desktopCloseTimeoutRef.current);
      desktopCloseTimeoutRef.current = null;
    }
    setIsDesktopCareersOpen(true);
  };

  const handleDesktopCareersLeave = () => {
    desktopCloseTimeoutRef.current = setTimeout(() => {
      setIsDesktopCareersOpen(false);
      desktopCloseTimeoutRef.current = null;
    }, 180);
  };

  const navigate = useNavigate();
  const location = useLocation();
  const isLandingArticleDetailsRoute = /^\/\d+$/.test(location.pathname);
  const canShowLandingNavigation =
    location.pathname === "/" ||
    location.pathname === "/contact-support" ||
    location.pathname === "/profile-setup" ||
    location.pathname === "/disclaimer" ||
    location.pathname === "/privacy-policy" ||
    location.pathname === "/terms-of-use" ||
    isLandingArticleDetailsRoute;

  const handleNavigationClick = () => {
    setIsPopupOpen(true);
    setIsMobileMenuOpen(false);
  };

  const closePopup = () => {
    setIsPopupOpen(false);
  };

  const handleLoginClick = () => {
    setIsPopupOpen(false);
    navigate("/", { state: { authMode: "signin" } });
  };

  const handleRegisterClick = () => {
    setIsPopupOpen(false);
    navigate("/", { state: { authMode: "register" } });
  };

  return (
    <>
      <header className="z-992 w-full bg-black text-white">
        <div className="flex items-center lg:justify-between justify-start gap-3 px-4 py-4 lg:px-6">
          {/* Mobile menu button (left) */}
          <button
            type="button"
            onClick={() => setIsMobileMenuOpen((prev) => !prev)}
            className="inline-flex items-center justify-center rounded p-1 text-white hover:bg-white/10 lg:hidden"
            aria-label="Toggle navigation"
          >
            {isMobileMenuOpen ? <MdClose size={22} /> : <MdMenu size={22} />}
          </button>

          {/* Logo and Alumni */}
          <div className="flex min-w-0 items-center lg:ml-0">
            <NavLink to="/" title="Deloitte" className="inline-flex shrink-0">
              <img src="/assets/img/deloitte_logo.svg" alt="Deloitte" className="max-h-4" />
            </NavLink>
            <div className="ml-3 border-l border-gray-600 pl-3 text-xl">Alumni</div>
          </div>

          {/* Navigation for desktop */}
          {canShowLandingNavigation && (
          <nav className="hidden items-center gap-1 lg:flex" aria-label="Landing navigation">
            {NAV_ITEMS.map((item) => {
              if (!item.children) {
                return (
                  <button
                    key={item.label}
                    type="button"
                    onClick={handleNavigationClick}
                    className="px-2 py-1 text-sm   text-white "
                  >
                    {item.label}
                  </button>
                );
              }
              return (
                <div
                  key={item.label}
                  className="relative"
                  onMouseEnter={handleDesktopCareersEnter}
                  onMouseLeave={handleDesktopCareersLeave}
                >
                  <button
                    type="button"
                    onClick={() =>
                      setIsDesktopCareersOpen((prev) => {
                        if (desktopCloseTimeoutRef.current) {
                          clearTimeout(desktopCloseTimeoutRef.current);
                          desktopCloseTimeoutRef.current = null;
                        }
                        return !prev;
                      })
                    }
                    className="px-2 py-1 text-sm  text-white"
                  >
                    <div className="flex gap-1 items-center">{item.label}<IoIosArrowDown /></div>
                  </button>
                  {isDesktopCareersOpen && (
                    <div
                      className="absolute left-0 top-full z-30 mt-2 w-40  bg-[#151515] py-1 shadow-lg"
                      onMouseEnter={handleDesktopCareersEnter}
                      onMouseLeave={handleDesktopCareersLeave}
                    >
                      {item.children.map((childItem) => (
                        <button
                          key={childItem}
                          type="button"
                          onClick={handleNavigationClick}
                          className="block w-full px-3 py-2 text-left text-xs text-white/90 hover:bg-white/10 hover:text-white"
                        >
                          {childItem}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
          )}

          <div className="w-7.5 lg:hidden" aria-hidden="true" />
        </div>

        {/* Mobile nav dropdown (full width below header) */}
        {isMobileMenuOpen && canShowLandingNavigation && (
          <div className="border-t border-white/20 px-4 pb-4 lg:hidden">
            <nav className="flex flex-col gap-1 pt-3" aria-label="Mobile landing navigation">
              {NAV_ITEMS.map((item) => {
                if (!item.children) {
                  return (
                    <button
                      key={item.label}
                      type="button"
                      onClick={handleNavigationClick}
                      className="rounded px-2 py-2 text-left text-sm text-white/90 hover:bg-white/10 hover:text-white"
                    >
                      {item.label}
                    </button>
                  );
                }
                return (
                  <div key={item.label} className="">
                    <button
                      type="button"
                      onClick={() => setIsMobileCareersOpen((prev) => !prev)}
                      className="flex w-full items-center justify-between px-2 py-2 text-left text-sm text-white/90 hover:bg-white/10 hover:text-white"
                    >
                      <span>{item.label}</span>
                      <IoIosArrowDown
                        className={`transition-transform ${isMobileCareersOpen ? "rotate-180" : "rotate-0"}`}
                        size={16}
                      />
                    </button>
                    {isMobileCareersOpen && (
                      <div className="border-t border-white/20 px-2 py-1">
                        {item.children.map((childItem) => (
                          <button
                            key={childItem}
                            type="button"
                            onClick={handleNavigationClick}
                            className="block w-full px-2 py-2 text-left text-sm text-white/80 hover:bg-white/10 hover:text-white"
                          >
                            {childItem}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
          </div>
        )}
      </header>

      {isPopupOpen && (
        <div className="fixed inset-0 z-1000 flex items-center justify-center bg-black/70 p-4">
          <div className="relative w-full max-w-200 overflow-hidden  bg-white shadow-2xl">
            <button
              type="button"
              onClick={closePopup}
              className="absolute right-3 top-3 z-20 rounded-full bg-white/90 p-1 text-[#0F172A] hover:bg-white"
              aria-label="Close popup"
            >
              <MdClose size={20} />
            </button>

            <div className="grid min-h-[70vh] grid-cols-1 md:grid-cols-2">
              <div className="h-full w-full">
                <img
                  src="/assets/img/popup-image.jpg"
                  alt="Alumni portal"
                  className="hidden md:block h-full w-full object-cover"
                  onError={(event) => {
                    event.currentTarget.src = "/assets/img/hero-bg.jpg";
                  }}
                />
                <img
                  src="/assets/img/mob-popup-image.jpg"
                  alt="Alumni portal"
                  className="block md:hidden h-full w-full object-cover"
                  onError={(event) => {
                    event.currentTarget.src = "/assets/img/mob-hero-bg.jpg";
                  }}
                />
              </div>

              <div className="flex items-center p-6 md:p-8">
                <div>
                  <h2 className="text-xl font-bold leading-7 text-black">
                    Please{" "}
                    <button type="button" className="underline hover:text-[#86BC25]" onClick={handleLoginClick}>log in</button>
                    {" "}or{" "}
                    <button type="button" className="underline  hover:text-[#86BC25]" onClick={handleRegisterClick}>register</button>
                    {" "}on the alumni portal to access these details.
                  </h2>
                  <p className="mt-4 text-sm leading-6 text-[#4B5563]">
                    Close this window to proceed with login or registration.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LandingHeader;
